import androidx.compose.runtime.*
import org.jetbrains.compose.web.attributes.*
import org.jetbrains.compose.web.dom.*

/** Tooltip shown on the unranked rows (players below the games threshold, spec 5.2). */
private const val UNRANKED_TOOLTIP =
    "Méně než 30 % možných her týmu (odehrané zápasy týmu × 3) — bez pořadí"

/** Milestone tier for the "Max" column -- highest threshold reached, or null below 200. */
private fun maxScoreMilestone(score: Int): String? = when {
    score >= 250 -> "milestone-250"
    score >= 220 -> "milestone-220"
    score >= 210 -> "milestone-210"
    score >= 200 -> "milestone-200"
    else -> null
}

/**
 * Shared player-ranking table used by both the global (season) and per-league rankings. Players
 * below the games threshold are moved to an unranked group at the end (no rank number, still
 * sorted by average) — see [partitionByGamesThreshold].
 */
@Composable
fun PlayerRankingTable(players: List<PlayerStatDto>) {
    val (ranked, unranked) = partitionByGamesThreshold(players)
    Table(attrs = { classes("sticky-2") }) {
        Tr {
            Th { Text("#") }
            Th { Text("Hráč") }
            Th { Text("Tým") }
            Th { Text("Hry") }
            Th { Text("Min") }
            Th { Text("Max") }
            Th(attrs = { classes("col-accent") }) { Text("Průměr") }
        }
        ranked.forEachIndexed { index, p ->
            Tr {
                Td { RankBadge(index + 1) }
                PlayerStatCells(p)
                Td { Span(attrs = { classes("pill") }) { Text(formatAvg(p.average)) } }
            }
        }
        unranked.forEachIndexed { index, p ->
            Tr(attrs = {
                classes(*(listOfNotNull("unranked-row", "unranked-first".takeIf { index == 0 }).toTypedArray()))
                attr("title", UNRANKED_TOOLTIP)
            }) {
                Td { }
                PlayerStatCells(p)
                Td { Text(formatAvg(p.average)) }
            }
        }
    }
}

/** The name/team/games/min/max cells common to ranked and unranked rows (average differs, so it's separate). */
@Composable
private fun PlayerStatCells(p: PlayerStatDto) {
    Td { Text(p.playerName) }
    Td { Text(p.teamName) }
    Td { Text(p.games.toString()) }
    Td { Text(p.minScore.toString()) }
    Td {
        val milestone = maxScoreMilestone(p.maxScore)
        if (milestone != null) {
            Span(attrs = { classes("max-score", milestone) }) { Text(p.maxScore.toString()) }
        } else {
            Text(p.maxScore.toString())
        }
    }
}
